export const useServiceLevelObjectivesStore = defineStore('serviceLevelObjectives', {
  state: () => ({
    serviceLevelObjectives: null as PaginatedServiceLevelObjective | null,
  }),
  getters: {
    getServiceLevelObjectives: (state) =>
      state.serviceLevelObjectives !== null ? state.serviceLevelObjectives.items : [],
  },
  actions: {
    setServiceLevelObjectives(serviceLevelObjectives: PaginatedServiceLevelObjective) {
      useLocalStorage().setItem('serviceLevelObjectives', serviceLevelObjectives);
      this.serviceLevelObjectives = serviceLevelObjectives;
    },
    async createServiceLevelObjective(
      serviceLevelObjective: ServiceLevelObjectiveCreateUpdate,
    ): Promise<ServiceLevelObjective | undefined> {
      try {
        const {data} = await apiPostServiceLevelObjective(serviceLevelObjective);
        pushDataToPaginatedState(this.serviceLevelObjectives, data);
        return data;
      } catch (error) {
        throw apiErrorMessage(error);
      }
    },
    async readServiceLevelObjectives({
      offset,
      limit,
      search,
    }: {offset?: number; limit?: number; search?: string} = {}): Promise<
      PaginatedServiceLevelObjective | undefined
    > {
      // Parse the local storage value & set the state.
      const localStorageServiceLevelObjectives: PaginatedServiceLevelObjective | null =
        useLocalStorage().getItem('serviceLevelObjectives');
      if (localStorageServiceLevelObjectives)
        this.setServiceLevelObjectives(localStorageServiceLevelObjectives);
      try {
        const {data} = await apiGetServiceLevelObjectives(limit, offset, search);
        this.setServiceLevelObjectives(data);
        return data;
      } catch (error) {
        throw apiErrorMessage(error);
      }
    },
    async readServiceLevelObjectiveById(
      id: string | number,
    ): Promise<ServiceLevelObjective | undefined> {
      // Parse & return the local storage value.
      const localStorageServiceLevelObjectives: PaginatedServiceLevelObjective | null =
        useLocalStorage().getItem('serviceLevelObjectives');
      if (localStorageServiceLevelObjectives) {
        const foundServiceLevelObjective = localStorageServiceLevelObjectives.items.find(
          (slo) => slo.id === id,
        );
        if (foundServiceLevelObjective !== undefined) return foundServiceLevelObjective;
      }
      try {
        const {data} = await apiGetServiceLevelObjective(id);
        return data;
      } catch (error) {
        throw apiErrorMessage(error);
      }
    },
    async updateServiceLevelObjective(
      serviceLevelObjective: ServiceLevelObjectiveCreateUpdate,
    ): Promise<ServiceLevelObjective | undefined> {
      try {
        const {data} = await apiPutServiceLevelObjective(serviceLevelObjective);
        // Push the object to state.
        if (this.serviceLevelObjectives !== null) {
          const index = this.serviceLevelObjectives.items.findIndex((c) => c.id === data.id);
          this.serviceLevelObjectives.items[index] = data;
          useLocalStorage().setItem('serviceLevelObjectives', this.serviceLevelObjectives);
        }
        return data;
      } catch (error) {
        throw apiErrorMessage(error);
      }
    },
    async deleteServiceLevelObjective(id: string | number): Promise<void> {
      try {
        await apiDeleteServiceLevelObjective(id);
        // Remove the object from state.
        if (this.serviceLevelObjectives !== null) {
          const index = this.serviceLevelObjectives.items.findIndex((c) => c.id === id);
          this.serviceLevelObjectives.items.splice(index, 1);
          useLocalStorage().setItem('serviceLevelObjectives', this.serviceLevelObjectives);
          this.serviceLevelObjectives.total -= 1;
        }
      } catch (error) {
        throw apiErrorMessage(error);
      }
    },
    /**
     * Penalties
     */
    async createPenalty(
      serviceLevelObjective: ServiceLevelObjectivePenaltyCreateUpdate,
    ): Promise<ServiceLevelObjectivePenalty | undefined> {
      try {
        const {data} = await apiPostPenalty(serviceLevelObjective);
        // Push the object to state.
        if (this.serviceLevelObjectives !== null) {
          const index = this.serviceLevelObjectives.items.findIndex((s) => s.id === data.slo_id);
          if (index >= 0) {
            this.serviceLevelObjectives.items[index].penalties.push(data);
            useLocalStorage().setItem('serviceLevelObjectives', this.serviceLevelObjectives);
          }
        }
        return data;
      } catch (error) {
        throw apiErrorMessage(error);
      }
    },
    async readPenaltyById(id: string | number): Promise<ServiceLevelObjectivePenalty | undefined> {
      // Parse & return the local storage value.
      const localStorageServiceLevelObjectives: PaginatedServiceLevelObjective | null =
        useLocalStorage().getItem('serviceLevelObjectives');
      if (localStorageServiceLevelObjectives) {
        for (const slo of localStorageServiceLevelObjectives.items) {
          const foundPenalty = slo.penalties.find((o) => o.id === id);
          if (foundPenalty !== undefined) return foundPenalty;
        }
      }
      try {
        const {data} = await apiGetPenalty(id);
        return data;
      } catch (error) {
        throw apiErrorMessage(error);
      }
    },
    async updatePenalty(
      serviceLevelObjective: ServiceLevelObjectivePenaltyCreateUpdate,
    ): Promise<ServiceLevelObjectivePenalty | undefined> {
      try {
        const {data} = await apiPutPenalty(serviceLevelObjective);
        // Find & update the target object.
        if (this.serviceLevelObjectives !== null) {
          // Find the slo
          const sindex = this.serviceLevelObjectives.items.findIndex((s) => s.id === data.slo_id);
          if (sindex >= 0) {
            // Find the penalty in the slo
            const pindex = this.serviceLevelObjectives.items[sindex].penalties.findIndex(
              (p) => p.id === data.id,
            );
            if (sindex >= 0) {
              Object.assign(this.serviceLevelObjectives.items[sindex].penalties[pindex], data);
              useLocalStorage().setItem('serviceLevelObjectives', this.serviceLevelObjectives);
            }
          }
        }
        return data;
      } catch (error) {
        throw apiErrorMessage(error);
      }
    },
    async deletePenalty(id: string | number): Promise<void> {
      try {
        const {data} = await apiDeletePenalty(id);
        // Find & remove the element.
        if (this.serviceLevelObjectives !== null) {
          // Find the slo
          const sindex = this.serviceLevelObjectives.items.findIndex((s) => s.id === data.slo_id);
          if (sindex >= 0) {
            // Find the penalty in the slo
            const pindex = this.serviceLevelObjectives.items[sindex].penalties.findIndex(
              (p) => p.id === data.id,
            );
            if (pindex >= 0) {
              this.serviceLevelObjectives.items[sindex].penalties.splice(pindex, 1);
              useLocalStorage().setItem('serviceLevelObjectives', this.serviceLevelObjectives);
            }
          }
        }
      } catch (error) {
        throw apiErrorMessage(error);
      }
    },
  },
});
